import type { NavItem } from "./useNavItems";

const dedupe = (items: NavItem[]): NavItem[] => {
  const seen = new Set<string>();
  return items.filter((item) => {
    const key = item.url ?? item.action;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// Substring match on label/section with label-prefix matches ranked first;
// an empty query matches nothing, and a page linked from two menu sections
// is listed once.
export function filterItems(items: NavItem[], query: string): NavItem[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const matches = dedupe(items).filter(
    (item) =>
      item.label.toLowerCase().includes(q) ||
      item.section.toLowerCase().includes(q),
  );
  matches.sort((a, b) => {
    const aStarts = a.label.toLowerCase().startsWith(q) ? 0 : 1;
    const bStarts = b.label.toLowerCase().startsWith(q) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    return a.label.localeCompare(b.label);
  });
  return matches;
}

/** Every tool for an empty query: the pinned ones first, in pin order, then the rest. */
export function pinnedFirst(items: NavItem[], pins: ReadonlyArray<string>): NavItem[] {
  const all = dedupe(items);
  const pinned = pins.flatMap((action) => all.filter((item) => item.action === action));
  return [...pinned, ...all.filter((item) => !pins.includes(item.action))];
}
